<template>
  <GridLayout
    v-if="page && page.id"
    ref="gridLayout"
    class="component-template editor-canvas"
    :class="{
      'editor--placeholder-hidden': placeholderHidden,
      'editor--active': isActive,
    }"
    :id="`canvas-${this.page.id}`"
    :col-num="colNum"
    :row-height="rowHeight"
    :is-draggable="mode === 'EDIT'"
    :is-resizable="mode === 'EDIT'"
    :margin="gridMargin"
    :layout="copiedModuleLayout"
    :vertical-compact="false"
    :prevent-collision="preventCollision"
    :max-rows="height"
    :use-css-transforms="false"
    :allow-overlap="gridIsDisabled"
    :allow-out-of-bounds="gridIsDisabled"
    :use-placeholder="!gridIsDisabled"
    :snap-grid="snapGrid"
    :expand-height-to-max-rows="true"
    :auto-size="true"
    :spacing-below-canvas="spacingBelowCanvas"
    @height-updated="$emit('height-updated', $event)"
    @layout-updated="onGridLayoutUpdated($event, modules, 'modules')"
  >
    <GridBackground
      :col-num="colNum"
      :row-height="rowHeight"
      :margin="gridMargin"
      v-if="showGridBackground"
    />
    <template
      v-for="{ mod, layout } in copiedModules"
      :key="mod.id"
    >
      <div
        v-if="mode === 'EDIT'"
        v-show="mod.id === activeModule"
        ref="gridItemBeforeContainers"
        :id="`grid-item-before-container-${mod.id}`"
        class="grid-canvas__grid-item-before-container"
      />
      <GridItem
        :x="layout.x"
        :y="layout.y"
        :z="moduleZIndexes[mod.id]"
        :w="layout.w"
        :h="layout.h"
        :r="layout.r"
        :i="mod.id"
        :scale="scale"
        @move="onMove"
        @resize="onResize"
        @rotation-start="busEmit('module:rotation-start', { id: mod.id, ...$event })"
        @rotation-change="busEmit('module:rotation-change', { id: mod.id, ...$event })"
        @rotation-end="busEmit('module:rotation-end', { id: mod.id, ...$event })"
        :is-resizable="!isLocked(mod.id) && mode === 'EDIT'"
        :is-resizable-y="!isLocked(mod.id) && !autoSizeShrinkEnabled(mod, 'modules')"
        :is-draggable="!isLocked(mod.id) && mode === 'EDIT'"
        :allow-rotation="activeModule === mod.id && mode === 'EDIT'
          && gridIsDisabled && !isLocked(mod.id)"
        ref="gridItems"
        :id="`module-${mod.id}`"
        :drag-ignore-from="EDITOR_MODULE_IGNORE_DRAG_SELECTOR"
        :class="[
          {
            'vue-grid-item--active': mod.id === activeModule,
            'vue-grid-item--preview': mod.type === 'preview',
          },
        ]"
      >
        <ModuleWrapper
          :active="mod.id === activeModule"
          :mode="mode"
          :module-data="mod"
          :module-name="moduleTypeToLabel(mod.type)"
          canvas-name="modules"
          :scale="scale"
          ref="moduleWrappers"
          :id="mod.id"
          :get-after-module-container-ref="getAfterModuleContainerRef"
          :module-animations-enabled="moduleAnimationsEnabled && isDisplayed(mod)"
        >
          <component
            :is="moduleTypeToComponent(mod.type)"
            :id="mod.id"
            :active="mod.id === activeModule"
            :mode="mode"
            :module-data="mod"
            :layout-data="layout"
            :scale="scale"
            canvas-name="modules"
            :get-before-module-container-ref="getBeforeModuleContainerRef"
            :is-displayed="isDisplayed(mod)"
            :page="page"
          />
          <template #module-options>
            <component
              :is="moduleTypeToOptions(mod.type)"
              :id="mod.id"
              :module-data="mod"
              canvas-name="modules"
            />
          </template>
        </ModuleWrapper>
        <template v-if="mode === 'EDIT' && !isLocked(mod.id)">
          <GridItemDragHandle
            position="top"
            :mod-type="mod.type"
          />
          <GridItemDragHandle
            position="end"
            :mod-type="mod.type"
          />
          <GridItemDragHandle
            position="bottom"
            :mod-type="mod.type"
          />
          <GridItemDragHandle
            position="start"
            :mod-type="mod.type"
          />
        </template>
      </GridItem>
      <div
        v-if="mode === 'EDIT'"
        v-show="mod.id === activeModule"
        ref="gridItemAfterContainers"
        :id="`grid-item-after-container-${mod.id}`"
        class="grid-canvas__grid-item-after-container"
      />
    </template>
    <CollaborativeEditable
      :input-name="dragHandleId"
      :style="dragStyle"
      :class="{ 'drag-handle-wrapper--invisible': !showDragHandle }"
      class="drag-handle-wrapper"
      v-if="draftType === 'board' && mode === 'EDIT'"
    >
      <div
        @mousedown="onPageHandleMousedown"
        @touchstart="onPageHandleMousedown"
        @dragstart="onHandleDragStart"
        @touchmove="onHandleDragStart"
        class="drag-handle"
        ref="dragHandle"
        :class="{ 'drag-handle--grabbing' : isGrabbing }"
      >
        <NebulaIcon
          size="xl"
          class-override="comet-button__icon"
          symbol-id="move-vertical--filled"
        />
      </div>
    </CollaborativeEditable>
  </GridLayout>
</template>

<script>
import interact from 'interactjs';
import { mapActions, mapState } from 'pinia';
import { NebulaIcon } from '@discoveryedu/nebula-components';
import { GridLayout, GridItem } from '@discoveryedu/vue-grid-layout';
import {
  find, get, throttle,
} from 'lodash-es';
import GridItemDragHandle from '@/components/GridItemDragHandle.vue';
import {
  moduleTypeToComponent,
  moduleTypeToOptions,
  moduleTypeToLabel,
} from '@/lib/utils';
import {
  EDITOR_CANVAS_GRID_MARGIN,
  EDITOR_CANVAS_GRID_ROW_HEIGHT,
  EDITOR_CANVAS_GRID_COL_NUM,
  EDITOR_CANVAS_NO_GRID_ROW_HEIGHT,
  EDITOR_CANVAS_NO_GRID_MARGIN,
  EDITOR_CANVAS_SLIDES_MAX_ROWS,
  EDITOR_CANVAS_WIDTH,
  EDITOR_CANVAS_HEIGHT,
  EDITOR_MODULE_IGNORE_DRAG_SELECTOR,
} from '@/lib/constants';
import ModuleWrapper from '@/components/ModuleWrapper.vue';
import modules from '@/components/modules/index';
import * as types from '@/lib/constants/store';
import CometBlockMessage from '@/components/ui-elements/CometBlockMessage.vue';
import GridBackground from '@/components/GridBackground.vue';
import { bus } from '@/lib/eventBus';

import { createTopZPosition, formatModuleLayoutForGrid, bottomYPosition } from '@/lib/utils/grid';
import canvas from '@/mixins/canvas';
import autoSizeEnabled from '@/mixins/autoSizeEnabled';
import CollaborativeEditable from '@/components/CollaborativeEditable.vue';

import {
  useEditorStore,
} from '@/stores';

const DRAG_HANDLE_OFFSET = -25;
const MIN_HEIGHT = 352;
const MAX_CHANGE = 30;

// give the drag handle a random id so that it can be tracked and locked
// CLEAR_CURRENTLY_EDITING requires a uuid, so reverting back to a guid here
const DRAG_HANDLE_ID = '86b3c192-c18d-4526-a7b2-15717f98a322';

export default {
  components: {
    GridItem,
    GridLayout,
    ModuleWrapper,
    GridBackground,
    CometBlockMessage,
    GridItemDragHandle,
    NebulaIcon,
    CollaborativeEditable,
    ...modules, // merge dynamic components
  },
  mixins: [canvas, autoSizeEnabled],
  emits: ['height-updated'],
  computed: {
    isActive() {
      return this.currentPage.id === this.pageId;
    },
    ...mapState(useEditorStore, {
      orderedModules: 'getOrderedModules',
      orderedModulesByPageId: 'getOrderedModulesByPageId',
      connectedUser: 'currentUserConnection',
    }),
    ...mapState(useEditorStore, [
      'currentPage',
      'currentlyEditing',
      'isLocked',
      'isPageHost',
      'draftType',
      'modulesOrderedByZPosition',
      'getModulesByPageIdOrderedByZPosition',
      'displayedModuleIds',
      'draft',
      'editorDrawers',
      'session',
    ]),
    dragHandleId() {
      return DRAG_HANDLE_ID;
    },
    page() {
      return this.pageId
        ? find(this.draft.pages, (pg) => pg.id === this.pageId)
        : this.currentPage;
    },
    gridIsDisabled() {
      return get(this, 'page.options.grid_disabled', false);
    },
    copiedModuleLayout() {
      return this.copiedModules.map((item) => item.layout);
    },
    modules() {
      return this.pageId
        ? this.orderedModulesByPageId(this.pageId)
        : this.orderedModules;
    },
    placeholderHidden() {
      // The placeholder module is always hidden in VIEW mode
      // and while dragging onto the canvas from the Add Module menu
      return this.mode === 'VIEW' || this.isDraggingFromAddModule;
    },
    maxRows() {
      if (this.draftType === 'board' && this.height) {
        return this.height;
      }

      return this.gridIsDisabled
        ? EDITOR_CANVAS_HEIGHT
        : EDITOR_CANVAS_SLIDES_MAX_ROWS;
    },
    colNum() {
      return this.gridIsDisabled ? EDITOR_CANVAS_WIDTH : EDITOR_CANVAS_GRID_COL_NUM;
    },
    rowHeight() {
      return this.gridIsDisabled ? EDITOR_CANVAS_NO_GRID_ROW_HEIGHT : EDITOR_CANVAS_GRID_ROW_HEIGHT;
    },
    gridMargin() {
      return this.gridIsDisabled
        ? [EDITOR_CANVAS_NO_GRID_MARGIN, EDITOR_CANVAS_NO_GRID_MARGIN]
        : [EDITOR_CANVAS_GRID_MARGIN, EDITOR_CANVAS_GRID_MARGIN];
    },
    preventCollision() {
      return !this.gridIsDisabled;
    },
    moduleZIndexes() {
      if (!this.gridIsDisabled) return {};
      /*
        If the grid is disabled, then assign z-indexes to modules based
        on their current z_position.
      */
      const modulesByZPosition = this.pageId
        ? this.getModulesByPageIdOrderedByZPosition(this.pageId)
        : this.modulesOrderedByZPosition;
      return modulesByZPosition.reduce((accumulator, mod, modIndex) => {
        const zIndexes = accumulator;
        zIndexes[mod.id] = modIndex + 1;
        return zIndexes;
      }, {});
    },
    spacingBelowCanvas() {
      if (this.isBoard) {
        return Math.max(50, Math.round(200 / this.scale)) + this.draggingDistance;
      }
      return 0;
    },
    isBoard() {
      return this.draftType === 'board';
    },
    dragStyle() {
      return {
        top: `${this.height + DRAG_HANDLE_OFFSET}px`,
      };
    },
    pageHeight() {
      let storedHeight = this.page?.options?.height;
      const bottomY = bottomYPosition(this.page?.modules || []);

      if (!storedHeight && this.draftType === 'board') {
        storedHeight = bottomY || this.maxRows;
      } else if (!storedHeight) {
        storedHeight = this.maxRows;
      }

      return storedHeight;
    },
  },
  watch: {
    pageHeight(value) {
      this.height = value;
    },
    session() {
      this.$nextTick(() => {
        if (this.activeModule && !this.currentlyEditing(this.activeModule)) {
          this.activeModule = null;
        }
      });
    },
    modules: {
      handler() {
        // Update local modules with Pinia data after API calls
        this.updateCopiedModules();
        this[types.CACHE_GRID_MATRIX]({ columns: this.colNum, rows: this.rowNum });

        /*
          Anytime changes are made to the list of modules for the current page,
          the 'layout' prop for the GridLayout component will be updated with these
          changes.
          When two or more modules have overlapping coordinates or dimensions, we
          wait for the GridLayout component to resolve these conflicts internally.
          Then we get the resolved layout and push these changes back into Pinia
          and the SharedDocument so they propagate to other clients and are persisted
          to the server.
          Only the host user for the current page will commit resolved grid conflicts to avoid
          neverending cyclical updates between multiple clients.
        */
        if (this.isPageHost(this.pageId)) {
          this.$nextTick(() => {
            const layout = get(this, '$refs.gridLayout.layout');
            if (layout) {
              this.onGridLayoutUpdated(layout, this.modules, 'modules', false);
            }
          });
        }
      },
      deep: true,
    },
    mode() {
      if (this.mode === 'VIEW') {
        this.activeModule = null;
      }
    },
  },
  created() {
    // Capture Vue model instance so we don't lose context in arrow functions
    const vm = this;

    // Store a local copy of the state's modules so that the layout is able to mutate it
    this.updateCopiedModules();

    // Initialize a module to be previewed and/or added
    bus.on('initPlaceholderModule', (data) => {
      if (data.canvasName === 'modules') {
        this.placeholderModule.type = data.type;
        this.placeholderModule.name = data.name;
        this.placeholderModule.options = data.options;
        this.placeholderModule.layout = data.layout;
      }
    });

    bus.on('updateDraggableXY', (data) => {
      if (data.canvasName === 'modules') {
        vm.x_position = data.x;
        vm.y_position = data.y;
      }
    });

    bus.on('activeModule', (data) => {
      if (data.canvasName === 'modules') {
        vm.setActive(data.id);
        this[types.UPDATE_CURRENTLY_EDITING]({
          [data.id]: this.connectedUser?.id,
        });
      }
    });

    // if we didn't have a height value in the db, we need to update
    if (!this.currentPage.options?.height && this.connectedUser?.isHost && this.mode === 'EDIT') {
      this[types.UPDATE_PAGE_HEIGHT](Math.max(this.pageHeight, MIN_HEIGHT));
    }

    // set the height
    this.height = this.pageHeight;

    // throttled handler for updating the height on
    this.updateHeightThrottled = throttle((height) => {
      this[types.UPDATE_PAGE_HEIGHT](height);
    }, 50);
  },
  name: 'EditorCanvas',
  data() {
    return {
      activeModule: null,
      copiedModules: [],
      showGridBackground: false,
      placeholderModule: {
        // We don't need an ID property because it will be assigned in Pinia action
        type: String,
        name: String,
        layout: Object,
        options: Object,
      },
      isDraggingFromAddModule: false,
      tempId: '00000000',
      x_position: 0,
      y_position: 0,
      dropped: false,
      EDITOR_MODULE_IGNORE_DRAG_SELECTOR,
      height: 0,
      isGrabbing: false,
      draggingDistance: 0,
      showDragHandle: false,
    };
  },
  props: {
    mode: {
      type: String,
      default: 'VIEW', // out of ['EDIT', 'VIEW']
    },
    scale: {
      type: Number,
      default: 1,
    },
    scrollContainer: {
      type: HTMLElement,
      default: null,
    },
    pageId: {
      type: String,
      default: null,
    },
    menuContainer: {
      type: HTMLElement, // used in gridLayout mixin
      default: null,
    },
    snapGrid: {
      type: Object,
      default: undefined,
    },
    moduleAnimationsEnabled: {
      type: Boolean,
      default: true,
    },
    // Makes all the modules on the canvas have isDisplayed === true
    forceDisplayed: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_MODULE,
      types.CACHE_GRID_MATRIX,
      types.UPDATE_CURRENTLY_EDITING,
      types.UPDATE_PAGE_HEIGHT,
      types.CLEAR_CURRENT_USER_EDITING,
    ]),
    busEmit(...args) {
      bus.emit(...args);
    },
    moduleTypeToComponent,
    moduleTypeToOptions,
    moduleTypeToLabel,
    isDragHandleVisible() {
      const handleRect = this.$refs.dragHandle.getBoundingClientRect();
      const handleTop = handleRect.top;
      const handleBottom = handleRect.bottom;

      const scrollRect = this.scrollContainer.getBoundingClientRect();
      const containerTop = scrollRect.top;
      const containerBottom = window.innerHeight;
      return handleTop >= containerTop && handleBottom <= containerBottom;
    },
    isDisplayed(mod) {
      return this.forceDisplayed || this.displayedModuleIds.has(mod.id);
    },
    onBodyClick({ target }) {
      this.onGridElementClicked(target);
    },
    updateCopiedModules() {
      this.copiedModules = this.modules.map((mod) => ({
        mod,
        layout: formatModuleLayoutForGrid(mod),
      }));
    },
    onMove(moduleId) {
      this.resetDragging();
      bus.emit('module:move', moduleId);
    },
    onResize(moduleId) {
      this.resetDragging();
      bus.emit('module:resize', moduleId);
    },
    resetDragging() {
      this.isDraggingFromAddModule = false;
    },
    setActive(id) {
      this.activeModule = id;
    },
    async addModuleToState(mod) {
      const newModuleId = await this[types.ADD_MODULE]({
        canvasName: 'modules',
        payload: mod,
      });

      bus.emit('activeModule', {
        canvasName: 'modules',
        id: newModuleId,
      });

      this.dropped = true;
    },
    initDropZone() {
      const vm = this; // Current vue model, so we don't lose context in arrow functions
      let canvasEntered = null; // flag for drop area
      // Initialize drop zone and set up listeners
      const dropElement = this.$el.closest('.editor__zoom-content');
      if (interact.isSet(dropElement)) {
        interact(dropElement).unset();
      }
      interact(dropElement).dropzone({
        accept: '.add-module-drawer .add-module__list-item--modules',
        ondropactivate() {
          vm.isDraggingFromAddModule = true;
        },
        ondragenter() {
          // switch the enter flag to true and initalize a position on the grid for the preview
          canvasEntered = 'modules';
          const placeholderLayout = {
            x_position: vm.x_position,
            y_position: vm.y_position,
            width: vm.placeholderModule.layout.width,
            height: vm.placeholderModule.layout.height,
          };
          vm.placeholderModule.layout = placeholderLayout;

          // Create a temporary module so the user can preview placement on grid
          const previewModule = vm.createPreviewModule(placeholderLayout);

          // When the preview begins, create a temporary module in the copiedModules list
          // to render the preview in the gridLayout
          bus.emit('beginPreview', { canvasEntered });
          vm.copiedModules.push({
            mod: previewModule,
            layout: formatModuleLayoutForGrid(previewModule),
          });
        },
        ondragleave() {
          // When the preview ends, delete the temporary module from the copiedModules list
          canvasEntered = null;
          vm.copiedModules = vm.copiedModules.filter((item) => item.mod.type !== 'preview');
        },
        ondrop() {
          // When the floating module is dropped, create a new module on the preview's position
          // and send it to the state
          vm.placeholderModule.layout = {
            x_position: vm.x_position,
            y_position: vm.y_position,
            height: vm.placeholderModule.layout.height,
            width: vm.placeholderModule.layout.width,
          };

          if (vm.gridIsDisabled) {
            vm.placeholderModule.layout.z_position = createTopZPosition(
              vm.modulesOrderedByZPosition,
            );
          }

          vm.addModuleToState(vm.placeholderModule);
          canvasEntered = null;
        },
      });
    },
    createPreviewModule(layout) {
      // Stub object
      return {
        id: this.tempId, // Temporary id
        type: 'preview',
        name: this.$t('Preview'),
        options: {},
        layout,
      };
    },
    onCopy(e) {
      // copy if the user clicks ctrl/meta+c
      if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
        const textSelected = window.getSelection().toString() !== '';
        const isInputTarget = ['TEXTAREA', 'INPUT'].includes(document.activeElement.tagName);

        //  make sure there is no active text selection or focus on textarea/input fields
        if (!textSelected && !isInputTarget) {
          // emit the copymodule event, which the modules are listening for
          bus.emit('copyModule');
        }
      }
    },
    updateHeight(height, scrollTarget, alignToTop = false) {
      // set the new height
      this.height = height;

      if (scrollTarget) {
        // @todo how to make this smoother? a smooth transition has to much of a delay
        scrollTarget.scrollIntoView(alignToTop);
      }

      // update
      this.updateHeightThrottled(height);
    },
    onPageHandleMousedown(event) {
      if (
        (
          !this.currentlyEditing(DRAG_HANDLE_ID)?.id
          || this.currentlyEditing(DRAG_HANDLE_ID)?.id === this.connectedUser?.id
        ) && this.mode === 'EDIT'
      ) {
        const handle = this.$refs.dragHandle;
        const currentHeight = this.height; // grab the height at the moment of click
        let steadyInterval;
        let distance = null;
        this.isGrabbing = true; // sets the grabbing cursor
        // tracker to allow us to calculate distance
        let dragged = event.screenY || event.targetTouches[0].screenY;

        // lock the handle
        this[types.UPDATE_CURRENTLY_EDITING]({
          [DRAG_HANDLE_ID]: this.connectedUser?.id,
        });

        /**
         * If the user has dragged the mouse far enough,
         * constantly increase or decrease the height
         */
        const constantHeightChange = () => {
          // if there isn't already an interval running
          if (!steadyInterval) {
            steadyInterval = setInterval(() => {
              let calculatedDistance = distance;

              if (calculatedDistance < -MAX_CHANGE) {
                calculatedDistance = -MAX_CHANGE;
              } else if (calculatedDistance > MAX_CHANGE) {
                calculatedDistance = MAX_CHANGE;
              }

              const newHeight = this.height + calculatedDistance;

              // if the new height isn't smaller than the minimum allowed
              if (newHeight > MIN_HEIGHT) {
                // set the new height
                this.updateHeight(newHeight, handle, calculatedDistance < 0);
              }
            }, 100);
          }
        };

        const stopConstantChange = () => {
          if (steadyInterval) {
            clearInterval(steadyInterval);
            steadyInterval = null;
          }
        };

        const moveTo = (y) => {
          // if we have a starting point
          if (dragged) {
            // figure out what the change in Y should represent
            distance = Math.round((y - dragged) / this.scale);

            // only apply changes when the distance isn't 0
            if (distance) {
              // determine what the new height should be with this difference
              const newHeight = currentHeight + distance;
              const gridBounds = this.scrollContainer.getBoundingClientRect();

              // if we've gone beyond the bounds of the container, start autoscrolling
              if (y > window.outerHeight - 10 || y < gridBounds.top + 96) {
                constantHeightChange();
              } else if (newHeight > MIN_HEIGHT) {
                // turn off steady change if it's running
                stopConstantChange();

                if (distance < 0) {
                  // set the drag distance when making the board smaller
                  // this allows us to 'freeze' the height to prevent some jumpiness
                  this.draggingDistance = distance * -1;
                }
                // set the new height
                this.updateHeight(newHeight);
              } else {
                stopConstantChange();
              }
            }
          }
        };

        /**
         * Handler for mouse drag
         */
        const onMouseMove = (mouseMoveEvent) => {
          moveTo(mouseMoveEvent.screenY);
          mouseMoveEvent.preventDefault();
        };

        /**
         * Handler for touch drag
         */
        const onTouchMove = (touchMoveEvent) => {
          moveTo(touchMoveEvent.targetTouches[0].screenY);
        };

        /**
         * Clean up all event listeners and return data to default state
         */
        const cleanUp = () => {
          // remove mouse and touch listener
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', cleanUp);
          window.removeEventListener('mouseleave', cleanUp);
          document.removeEventListener('touchmove', onTouchMove);
          document.removeEventListener('touchend', cleanUp);

          // clear editing block
          this[types.CLEAR_CURRENT_USER_EDITING]();

          // reset drag tracker
          dragged = null;

          // make sure we're not automatically moving
          stopConstantChange();

          // clear grabbing cursor
          this.isGrabbing = false;

          // release the height override
          this.draggingDistance = false;
          if (this.height < MIN_HEIGHT + 150) {
            this.scrollContainer.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          } else if (!this.isDragHandleVisible() && handle.getBoundingClientRect().top - 96 < 0) {
            // scroll to something acceptable
            this.scrollContainer.scrollTo({ top: Math.round(this.height), behavior: 'smooth' });
          } else {
            this.scrollContainer.scrollTo({
              top: Math.round(this.height * this.scale),
              behavior: 'smooth',
            });
          }
        };

        // apply mousemove or touchmove at document level
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('touchmove', onTouchMove);

        // on drop, we're done
        document.addEventListener('mouseup', cleanUp);
        window.addEventListener('mouseleave', cleanUp);
        document.addEventListener('touchend', cleanUp);
      }
    },
    onHandleDragStart() {
      return false;
    },
    updateShowGrabHandleOnMouseMove(event) {
      if (this.mode === 'EDIT') {
        const handleRect = this.$refs.dragHandle.getBoundingClientRect();
        const handleCenter = (handleRect.top + handleRect.bottom) / 2;
        const mouseDistanceToHandle = Math.abs(handleCenter - event.pageY);
        if (
          !this.isGrabbing
          && mouseDistanceToHandle < 250
        ) {
          this.showDragHandle = true;
        } else if (!this.isGrabbing) {
          this.showDragHandle = false;
        }
      }
    },
  },
  mounted() {
    bus.on('document:mousedown', this.onBodyClick);
    this.initDropZone();
    this[types.CACHE_GRID_MATRIX]({ columns: this.colNum, rows: this.rowNum });
    if (this.mode === 'EDIT') {
      bus.on('document:keydown', this.onCopy);
    }

    if (this.mode === 'EDIT' && this.draftType === 'board') {
      document.addEventListener('mousemove', this.updateShowGrabHandleOnMouseMove);
    }
  },
  beforeUnmount() {
    bus.off('document:mousedown', this.onBodyClick);
    if (this.mode === 'EDIT') {
      bus.off('document:keydown', this.onCopy);
    }
    interact('.editor__contents').unset();

    document.removeEventListener('mousemove', this.updateShowGrabHandleOnMouseMove);
  },
};
</script>

<style lang="stylus">

.component-template {
  min-height: 352px; // prevent boards shorter than seven rows
  max-height: 150%;
}

.editor-canvas {
  z-index: 2; // Ensures everything on canvas is above backgrounds
}

.editor--placeholder-hidden .vue-grid-placeholder {
  display: none;
}

.drag-handle-wrapper {
  border-radius: 50px;
  height: 50px;
  left: 0;
  margin-left: auto;
  margin-right: auto;
  opacity: 1;
  position: absolute;
  right: 0;
  transition: opacity $nebula-transition-default;
  width: 50px;
  z-index: 999999;

  &--invisible {
    opacity: 0;
    transition: opacity $nebula-transition-default
    visibility: hidden;
  }
}

.drag-handle {
  background-color: white;
  border-radius: 50px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  cursor: grab;
  height: 32px;
  left: 50%;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 32px;

  &:hover {
    background-color: $nebula-color-platform-interactive-900;
  }

  &--grabbing {
    cursor: grabbing;
  }

  svg {
    height: 100%;
  }
}
</style>
